import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import cn from 'clsx';

import Collapsible from '../../library/collapsible/collapsible';
import CollapsibleToggle from '../../library/collapsible/components/collapsible-toggle/collapsible-toggle';
import IntroductionMessage from './components/introduction-message/introduction-message';
import TreadwellAiSolidIcon from '../../shared/icons/treadwell-ai-solid-icon';

import { GET_TREADWELL_GEN_AI } from './graphql/treadwell-product-explanation-queries';

import {
  useDataLayer,
  useFitment,
  useGraphQLClient,
  useMyAccount,
  useMyStore,
  usePrevious,
  useTreadwellPreferences
} from '../../shared/hooks/hooks';

import { trackAction } from '../../shared/utils/analytics/analytics';
import { interpolateCmsString } from '../../shared/utils/cms/cms';
import { getVehicleInput } from '../../shared/utils/fitment/fitment';
import { getTreadwellAITrackActionPrefix } from '../../shared/utils/product/product';
import { getTreadwellGQLInput } from '../../shared/utils/treadwell/treadwell';
import { isArrayWithLength } from '../../shared/utils/validators/validators';
import { pickRandomLoadingMessage } from './utils/treadwell-product-explanation-utils';

import {
  ErrorData,
  Maybe,
  ProductData,
  RequestTypeEnum
} from '../../shared/types/graphql-types';
import { TREADWELL_AI_TRACK_ACTION_KEYS } from '../../shared/constants/products';
import {
  TREADWELL_AI_LOADING_MESSAGES,
  TREADWELL_AI_MESSAGE_CHANGE_MS
} from './constants/treadwell-product-explanation-constants';

import styles from './treadwell-product-explanation.scss';

const Explanation = lazy(() => import('./components/explanation/explanation'));

interface TreadwellProductExplanationProps {
  className?: string;
  compareSection?: React.ReactNode;
  isComparingProducts?: boolean;
  isOpenByDefault?: boolean;
  products?: Array<Maybe<ProductData>>;
  requestType: RequestTypeEnum;
}

function TreadwellProductExplanation(props: TreadwellProductExplanationProps) {
  const {
    className,
    compareSection,
    isComparingProducts,
    isOpenByDefault = false,
    products = [],
    requestType
  } = props;
  const client = useGraphQLClient();
  const { myStore } = useMyStore();
  const { treadwellPreferences } = useTreadwellPreferences();
  const { selectedFitment } = useFitment();
  const [isToggleDisabled, setIsToggleDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<Maybe<string>>(null);
  const [explanation, setExplanation] = useState<Maybe<string>>(null);
  const [errors, setErrors] = useState<Maybe<Array<ErrorData>>>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [submittedFeedback, setSubmittedFeedback] = useState({});
  const { isFleetAccount } = useMyAccount();
  const { currentPageType } = useDataLayer();
  const isFleetEnabled =
    process.env.__FF_FLEET_ACCOUNT__ === 'true' && isFleetAccount;
  const isFleetTreadwell =
    process.env.__FF_FLEET_TREADWELL__ === 'true' && isFleetEnabled;
  const { isEveryProductTreadwell, treadwellCacheKey } = useMemo(() => {
    const treadwellData = {
      isEveryProductTreadwell: true,
      treadwellCacheKey: ''
    };

    for (const product of products) {
      if (!product?.recommendationData) {
        treadwellData.isEveryProductTreadwell = false;
      }

      if (!treadwellData.treadwellCacheKey) {
        treadwellData.treadwellCacheKey =
          product?.recommendationData?.treadwellSessionResponseCacheKey || '';
      }

      if (
        treadwellData.treadwellCacheKey &&
        !treadwellData.isEveryProductTreadwell
      ) {
        break;
      }
    }

    return treadwellData;
  }, [products]);

  const { zipCodeWeatherData } = treadwellPreferences || {};
  const { city } = zipCodeWeatherData || {};

  const loadingMessageIntervalRef: { current: NodeJS.Timeout | null } =
    useRef(null);
  const lastArrIndex = useRef<number>(-1);
  const lastMessage = useRef<Maybe<string>>(null);

  const previousProducts = usePrevious(products);

  const shouldUseDefaultExplanation = useMemo(
    () =>
      !isArrayWithLength(products) ||
      !treadwellPreferences ||
      !selectedFitment ||
      !isEveryProductTreadwell,
    [isEveryProductTreadwell, products, selectedFitment, treadwellPreferences]
  );
  const hasDefaultFetchedRef = useRef(false);
  const introductionMessageRef = useRef(null);
  const fetchCount = useRef(0);
  const collapsedButtonText = shouldUseDefaultExplanation
    ? 'Learn more'
    : 'See why';

  const handleTrackAction = useCallback(
    (id, obj = {}) => {
      const trackActionIdPrefix = getTreadwellAITrackActionPrefix(
        currentPageType,
        isComparingProducts
      );
      trackAction(`${trackActionIdPrefix}_${id}`, {
        ...obj,
        [TREADWELL_AI_TRACK_ACTION_KEYS.MESSAGE]: introductionMessageRef.current
      });
    },
    [isComparingProducts, currentPageType]
  );

  const handleErrors = useCallback(
    errors => {
      const errorMessage = errors?.[0]?.message;
      const errorReason = errors?.[0]?.extensions?.classification;
      handleTrackAction('error', {
        [TREADWELL_AI_TRACK_ACTION_KEYS.ERROR_REASON]: errorReason,
        [TREADWELL_AI_TRACK_ACTION_KEYS.ERROR_MESSAGE]: errorMessage
      });
      setErrors(errors);
      console.error(errorMessage);
    },
    [handleTrackAction]
  );

  const handleHelpfulClick = useCallback(
    (id, isHelpful) => {
      handleTrackAction('see_why_rate_response_click', {
        [TREADWELL_AI_TRACK_ACTION_KEYS.RESPONSE_RATING]: isHelpful
          ? 'helpful'
          : 'unhelpful'
      });
      setSubmittedFeedback(prev => ({
        ...prev,
        [id]: isHelpful
      }));
    },
    [handleTrackAction]
  );

  const handleMessageSet = useCallback(
    msg => {
      if (!introductionMessageRef.current) {
        introductionMessageRef.current = msg;

        handleTrackAction('view');
      }
    },
    [handleTrackAction]
  );

  const handleLoadingMessage = useCallback(
    arrIdx => {
      const msg = pickRandomLoadingMessage(arrIdx, lastMessage.current);
      lastMessage.current = msg;

      const interpolatedMessage = interpolateCmsString(
        {
          city: city
            ? city.charAt(0)?.toUpperCase() + city?.slice(1).toLowerCase()
            : "your city's"
        },
        msg
      );
      setLoadingMessage(interpolatedMessage);
    },
    [city]
  );

  const handleTreadwellClick = useCallback(
    () => handleTrackAction('learn_more_treadwell_click'),
    [handleTrackAction]
  );

  const fetchTreadwellExplanation = useCallback(
    async (forceFetch = false) => {
      if (
        (!shouldUseDefaultExplanation &&
          !isLoading &&
          !errors &&
          fetchCount.current === 0) ||
        forceFetch
      ) {
        const treadwell = getTreadwellGQLInput(
          selectedFitment,
          treadwellPreferences,
          isFleetTreadwell
        );
        const vehicleInfo = getVehicleInput(selectedFitment);
        const startTime = performance.now();

        setIsLoading(true);
        setErrors(null);
        fetchCount.current++;
        handleLoadingMessage(0);
        lastArrIndex.current = 0;

        loadingMessageIntervalRef.current = setInterval(() => {
          const timeElapsed = performance.now() - startTime;
          const maxArrs = TREADWELL_AI_LOADING_MESSAGES.length - 1;
          const arrIdx = Math.min(
            Math.floor(timeElapsed / TREADWELL_AI_MESSAGE_CHANGE_MS),
            maxArrs
          );
          if (arrIdx !== lastArrIndex.current) {
            handleLoadingMessage(arrIdx);
            lastArrIndex.current = arrIdx;
          }
        }, TREADWELL_AI_MESSAGE_CHANGE_MS);

        const { data, errors } = await client.query({
          fetchPolicy: 'network-only',
          query: GET_TREADWELL_GEN_AI,
          variables: {
            productInfo: products.map(product => ({
              frontProductCode: product!.code,
              rearProductCode: product!.rearProduct?.code
            })),
            requestType: requestType,
            sourceType: 'WEB',
            storeCode: myStore?.code,
            treadwellCacheKey: treadwellCacheKey,
            treadwellRequest: treadwell,
            vehicleInfo
          }
        });
        const endTime = performance.now();
        const loadTime = Math.round((endTime - startTime) / 1000);

        const explanationData = data?.product?.treadwellGenAI;

        if (explanationData) {
          handleTrackAction('see_why_loaded', {
            [TREADWELL_AI_TRACK_ACTION_KEYS.LOAD_TIME]: `${loadTime}s`
          });
          setExplanation(explanationData?.replace(/[•]/g, '-'));
        } else {
          handleErrors(
            errors || [{ message: 'Unable to fetch treadwell explanation' }]
          );
        }

        setIsToggleDisabled(false);
        setIsLoading(false);
        setLoadingMessage(null);
        clearInterval(loadingMessageIntervalRef.current);
        loadingMessageIntervalRef.current = null;
      }
    },
    [
      client,
      errors,
      handleErrors,
      handleLoadingMessage,
      handleTrackAction,
      isFleetTreadwell,
      isLoading,
      myStore?.code,
      products,
      requestType,
      selectedFitment,
      shouldUseDefaultExplanation,
      treadwellCacheKey,
      treadwellPreferences
    ]
  );

  const handleToggle = useCallback(() => {
    if (fetchCount.current === 0) {
      handleTrackAction(
        `${shouldUseDefaultExplanation ? 'learn_more' : 'see_why'}_click`
      );
    }

    setShowExplanation(!showExplanation);
    fetchTreadwellExplanation();
  }, [
    fetchTreadwellExplanation,
    handleTrackAction,
    shouldUseDefaultExplanation,
    showExplanation
  ]);

  const handleTryAgainClick = useCallback(() => {
    handleTrackAction('error_retry');
    fetchTreadwellExplanation(true);
  }, [fetchTreadwellExplanation, handleTrackAction]);

  useEffect(() => {
    if (
      previousProducts?.length !== products?.length &&
      products?.length > 1 &&
      fetchCount.current > 0
    ) {
      if (isLoading) {
        setShowExplanation(false);
        fetchCount.current = 0;
        setIsToggleDisabled(true);
      } else {
        fetchTreadwellExplanation(true);
      }
    }
  }, [
    fetchTreadwellExplanation,
    isLoading,
    previousProducts,
    products,
    showExplanation
  ]);

  useEffect(() => {
    if (isOpenByDefault && !hasDefaultFetchedRef.current) {
      hasDefaultFetchedRef.current = true;
      handleToggle();
    }
  }, [handleToggle, isOpenByDefault]);

  useEffect(
    () => () => {
      loadingMessageIntervalRef.current &&
        clearInterval(loadingMessageIntervalRef.current);
    },
    []
  );

  return (
    <div
      className={cn(
        `${styles['container']} ${
          isLoading ? styles['container--loading'] : ''
        }`,
        className
      )}
    >
      <div className={styles['collapsible-toggle-container']}>
        <TreadwellAiSolidIcon className={styles['treadwell-ai-icon']} />
        <div className={styles['introduction-container']}>
          <IntroductionMessage
            currentPageType={currentPageType}
            isComparingProducts={isComparingProducts}
            onMessageSet={handleMessageSet}
            products={products}
          />
          <CollapsibleToggle
            className={styles['collapsible-toggle']}
            collapseType="SECONDARY"
            isDisabled={isToggleDisabled}
            isOpen={showExplanation}
            label={showExplanation ? 'Hide details' : collapsedButtonText}
            toggleCollapsible={handleToggle}
            toggleType="CHEVRON"
          />
        </div>
      </div>
      <Collapsible
        className={styles['collapsible']}
        hideToggle
        isOpen={showExplanation}
      >
        <Suspense fallback={<div />}>
          <Explanation
            compareSection={compareSection}
            errors={errors}
            explanation={explanation}
            hideDisclaimer={shouldUseDefaultExplanation}
            hideReviewFeedback={shouldUseDefaultExplanation}
            id={treadwellCacheKey || products[0]?.code || ''}
            isLoading={isLoading}
            loadingMessage={loadingMessage}
            onError={handleErrors}
            onHelpfulClick={handleHelpfulClick}
            onTreadwellClick={handleTreadwellClick}
            onTryAgainClick={handleTryAgainClick}
            showErrorRefetchButton={fetchCount.current < 2}
            submittedFeedback={submittedFeedback}
          />
        </Suspense>
      </Collapsible>
    </div>
  );
}

export default TreadwellProductExplanation;
